/**
 * File items-loadmore.js.
 *
 * Items Loadmore on scroll.
 *
 * @package wildrivers
 */
import { elementIsVisibleInViewport } from './in-viewport';
import { WPAjax } from './wpajax';

export function itemsLoadmore() {
	const listings = document.querySelectorAll( '.filterable-listing' );
	if ( ! listings?.length ) {
		return;
	}

	listings.forEach( ( listing ) => {
		const itemsEl = listing.querySelector( '.items' );
		const wrap = listing.querySelector( '.items-loadmore-wrap' );
		const button = listing.querySelector( '.items-loadmore' );
		const trigger = listing.querySelector( '.items-loadmore-trigger' );
		if ( ! itemsEl || ! wrap || ( ! button && ! trigger ) ) {
			return;
		}

		let isLoading = false;

		const getState = () => {
			const currentPage = parseInt( listing.dataset.currentPage || '1', 10 );
			const maxPages = parseInt( listing.dataset.maxPages || '1', 10 );
			const perPage = parseInt( listing.dataset.perPage || '6', 10 );
			const postType = listing.dataset.postType;
			const taxonomy = listing.dataset.taxonomy || listing.querySelector( '.filters' )?.dataset.taxonomy;
			const term = listing.querySelector( '.filters' )?.dataset.active || 'all';
			return { currentPage, maxPages, perPage, postType, taxonomy, term };
		};

		const updateWrapVisibility = () => {
			const { currentPage, maxPages } = getState();
			if ( currentPage >= maxPages ) {
				wrap.style.display = 'none';
			} else {
				wrap.style.display = '';
			}
		};

		const loadNextPage = async () => {
			const { currentPage, maxPages, perPage, postType, taxonomy, term } = getState();
			if ( isLoading ) return;
			if ( currentPage >= maxPages ) {
				updateWrapVisibility();
				return;
			}

			isLoading = true;
			const nextPage = currentPage + 1;
			itemsEl.classList.add( 'is-loading' );

			try {
				const resText = await WPAjax( 'wr_items_loadmore', {
					postType,
					taxonomy,
					term,
					perPage,
					page: nextPage,
				} );
				const res = JSON.parse( resText );
				if ( ! res?.success ) {
					throw new Error( res?.data?.message || 'Request failed' );
				}

				if ( res.data.html ) {
					itemsEl.insertAdjacentHTML( 'beforeend', res.data.html );
				}

				listing.dataset.currentPage = String( nextPage );
				if ( res.data.maxPages !== undefined ) {
					listing.dataset.maxPages = String( res.data.maxPages );
				}

				updateWrapVisibility();
			} catch ( err ) {
				// eslint-disable-next-line no-console
				console.error( err );
			} finally {
				itemsEl.classList.remove( 'is-loading' );

				const newItems = itemsEl.querySelectorAll('.overlay-link');
				if ( newItems ) {
					newItems.forEach( (link) => {
						link.addEventListener( 'click', (e) => {
							e.preventDefault();
						});
					});
				}
				isLoading = false;
			}
		};

		// Button click (accessible fallback).
		if ( button ) {
			button.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				loadNextPage();
			} );
		}

		// Auto-load when trigger becomes visible.
		if ( trigger && 'IntersectionObserver' in window ) {
			const observer = new IntersectionObserver(
				( entries ) => {
					entries.forEach( ( entry ) => {
						if ( entry.isIntersecting ) {
							loadNextPage();
						}
					} );
				},
				{ root: null, rootMargin: '200px 0px', threshold: 0 }
			);
			observer.observe( trigger );
		} else if ( trigger ) {
			const onScroll = () => {
				if ( elementIsVisibleInViewport( trigger, true ) ) {
					loadNextPage();
				}
			};
			window.addEventListener( 'scroll', onScroll, { passive: true } );
			window.addEventListener( 'resize', onScroll, { passive: true } );
		}

		updateWrapVisibility();
	} );
}
